import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, UsersRound, X } from "lucide-react";
import DateRangePicker from "../../../../../ecommerce/globalcomponents/DateRangePicker";
import LoyaltyReferralTable from "../LoyaltyReferralTable";
import { AppDispatch, RootState } from "../../../../../store";
import { getLoyaltyReferralCommission } from "../../../../../Redux/referral/slice";
import {
  formatEarningsCurrency,
  mapLoyaltyItemToRecord,
} from "../../earningsAdapters";
import "../LoyaltyReferralIncentives.css";

const LoyaltyRefIncentive = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loyaltyReferralCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      getLoyaltyReferralCommission({
        page: currentPage,
        limit: pageSize,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, currentPage, pageSize, debouncedSearch, dateRange]);

  const { data, pagination, totalLoyaltyReferralCommission, loading, error } =
    loyaltyReferralCommission;

  const visibleRecords = useMemo(
    () => (data || []).map((item) => mapLoyaltyItemToRecord(item)),
    [data],
  );

  const totalPages = Math.max(1, pagination?.totalPages || 0);
  const page = Math.min(currentPage, totalPages);

  return (
    <section className="loyalty-referral-page">
      <div className="loyalty-referral-shell">
        <header className="loyalty-referral-header">
          <div className="loyalty-referral-heading">
            <span className="loyalty-referral-heading-icon">
              <UsersRound size={24} strokeWidth={2} />
            </span>
            <div>
              <span className="loyalty-referral-kicker">
                LOYALTY REFERRAL INCENTIVES
              </span>
              <h1>Loyalty Referral Incentives</h1>
              <p>
                Track loyalty referral earnings, partner network activity
                and incentive status.
              </p>
            </div>
          </div>

          <div className="loyalty-referral-summary">
            <span>Total Received</span>
            <strong>
              {formatEarningsCurrency(totalLoyaltyReferralCommission || 0)}
            </strong>
          </div>
        </header>

        <div className="loyalty-referral-toolbar">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Entries per page"
          >
            <option value={5}>5 Entries</option>
            <option value={10}>10 Entries</option>
            <option value={20}>20 Entries</option>
          </select>

          <div className="loyalty-referral-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="loyalty-referral-search">
            <Search size={18} strokeWidth={2} />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search loyalty incentives..."
              aria-label="Search loyalty referral incentives"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loyalty-referral-empty text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="loyalty-referral-empty text-center py-5 text-danger">
            {error}
          </div>
        ) : (
          <LoyaltyReferralTable rows={visibleRecords} />
        )}

        {!loading && !error && totalPages > 1 && (
          <footer className="loyalty-referral-pagination">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setCurrentPage(page - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setCurrentPage(page + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </footer>
        )}
      </div>
    </section>
  );
};

export default LoyaltyRefIncentive;
